import { ReactElement, useState, useEffect, useCallback } from 'react';
import { Link } from 'react-router-dom';
import BookmarkButton from '../../components/BookmarkButton';
import BookmarkBatchProvider from '../../components/BookmarkBatchProvider';
import {
  FileText,
  Search,
  ChevronRight,
  Loader2,
  BarChart3,
  TrendingUp,
  LineChart,
  RefreshCw,
  ChevronLeft,
  X,
} from 'lucide-react';

function reportAnalysisId(path: string): string {
  // Path may be absolute (https://api.finexus.net/reports/{id}) or relative (/reports/{id}).
  return path.replace(/^.*\/reports\//, '');
}

// API base URL - use proxy in development (empty string), or REACT_APP_API_URL in production
const API_BASE = process.env.REACT_APP_API_URL || '';

// Types for API data
interface ArticleSummary {
  id: number;
  title: string;
  subtitle: string;
  tickers: string[];
  category: string;
  section_number: number;
  section_name: string;
  provider: string;
  status: string;
  generated_at: string;
  time_ago: string;
  path: string;  // SEO-friendly URL path (e.g., /reports/section08_AAPL_20260202)
}

interface CategoryData {
  name: string;
  description: string;
  articles: ArticleSummary[];
  total_count: number;
}

interface PortalData {
  categories: Record<string, CategoryData>;
  total_articles: number;
}

interface ListResponse {
  articles: ArticleSummary[];
  total_count: number;
  limit: number;
  offset: number;
  has_more: boolean;
  page: number;
  total_pages: number;
}

// Category configuration
const CATEGORY_CONFIG: Record<string, {
  icon: ReactElement;
  color: string;
  bgColor: string;
  borderColor: string;
  name: string;
  description: string;
}> = {
  overview: {
    icon: <TrendingUp className="w-5 h-5" />,
    color: 'text-emerald-600',
    bgColor: 'bg-emerald-50',
    borderColor: 'border-emerald-200',
    name: 'Overview',
    description: 'High-level insights and investment thesis',
  },
  classification: {
    icon: <BarChart3 className="w-5 h-5" />,
    color: 'text-blue-600',
    bgColor: 'bg-blue-50',
    borderColor: 'border-blue-200',
    name: 'Company Profile',
    description: 'Factor tags, style, and competitive positioning',
  },
  historical: {
    icon: <LineChart className="w-5 h-5" />,
    color: 'text-indigo-600',
    bgColor: 'bg-indigo-50',
    borderColor: 'border-indigo-200',
    name: 'Financial History',
    description: 'Multi-year revenue, profitability, and efficiency trends',
  },
  fundamentals: {
    icon: <BarChart3 className="w-5 h-5" />,
    color: 'text-purple-600',
    bgColor: 'bg-purple-50',
    borderColor: 'border-purple-200',
    name: 'Deep Dive Fundamentals',
    description: 'Detailed financial statement and valuation analysis',
  },
  valuation: {
    icon: <BarChart3 className="w-5 h-5" />,
    color: 'text-orange-600',
    bgColor: 'bg-orange-50',
    borderColor: 'border-orange-200',
    name: 'Valuation',
    description: 'Valuation analysis and peer comparison',
  },
  macro: {
    icon: <TrendingUp className="w-5 h-5" />,
    color: 'text-teal-600',
    bgColor: 'bg-teal-50',
    borderColor: 'border-teal-200',
    name: 'Macroeconomic Context',
    description: 'Economic indicators, rates, inflation, and GDP trends',
  },
  risk: {
    icon: <BarChart3 className="w-5 h-5" />,
    color: 'text-red-600',
    bgColor: 'bg-red-50',
    borderColor: 'border-red-200',
    name: 'Risk Assessment',
    description: 'Financial health screening and risk alerts',
  },
  market: {
    icon: <TrendingUp className="w-5 h-5" />,
    color: 'text-cyan-600',
    bgColor: 'bg-cyan-50',
    borderColor: 'border-cyan-200',
    name: 'Stocks and Market',
    description: 'Trading and price behavior',
  },
  institutional: {
    icon: <BarChart3 className="w-5 h-5" />,
    color: 'text-violet-600',
    bgColor: 'bg-violet-50',
    borderColor: 'border-violet-200',
    name: 'Institutional & Insider Activity',
    description: 'Institutional ownership trends, position flows, and insider trading patterns',
  },
  sentiment: {
    icon: <BarChart3 className="w-5 h-5" />,
    color: 'text-pink-600',
    bgColor: 'bg-pink-50',
    borderColor: 'border-pink-200',
    name: 'Predictive Signals',
    description: 'Predictive signal analysis, institutional ownership dynamics, and insider activity',
  },
  context: {
    icon: <LineChart className="w-5 h-5" />,
    color: 'text-amber-600',
    bgColor: 'bg-amber-50',
    borderColor: 'border-amber-200',
    name: 'Context',
    description: 'Industry, macro, and ESG factors',
  },
};

// Report Card Component
interface ReportCardProps {
  report: ArticleSummary;
}

function ReportCard({ report }: ReportCardProps): ReactElement {
  const config = CATEGORY_CONFIG[report.category] || CATEGORY_CONFIG.overview;

  // Use the SEO-friendly path from API (e.g., /reports/section08_AAPL_20260202)
  const reportPath = report.path || `/reports/report_${report.id}`;
  const analysisId = reportAnalysisId(reportPath);

  return (
    <div className={`group relative bg-white border ${config.borderColor} rounded-lg overflow-hidden hover:shadow-md hover:border-slate-300 transition-all duration-200`}>
      <a href={reportPath} className="block">
        <div className="p-5">
          {/* Header: category badge + tickers + time */}
          <div className="flex items-center gap-2 mb-3 flex-wrap pr-8">
            <span className={`px-2 py-1 text-[10px] font-medium uppercase tracking-wide ${config.color} ${config.bgColor} rounded`}>
              {config.name}
            </span>
            {report.tickers.slice(0, 3).map((ticker) => (
              <span
                key={ticker}
                className="px-1.5 py-0.5 text-[10px] font-mono font-medium text-slate-600 bg-slate-100 rounded"
              >
                {ticker}
              </span>
            ))}
            {report.tickers.length > 3 && (
              <span className="text-[10px] text-slate-400">
                +{report.tickers.length - 3}
              </span>
            )}
            <span className="text-[10px] text-slate-400 ml-auto">{report.time_ago}</span>
          </div>

          {/* Title (subtitle shown on hover) */}
          <h3
            className="text-base font-medium text-slate-800 group-hover:text-slate-900 transition-colors line-clamp-3"
            style={{ fontFamily: 'Georgia, serif' }}
            title={report.subtitle || undefined}
          >
            {report.title}
          </h3>
        </div>
      </a>
      <div className="absolute top-2 right-2">
        <BookmarkButton
          itemType="report"
          itemId={analysisId}
          title={report.title}
          subtitle={report.tickers?.join(', ')}
          url={reportPath}
          size="sm"
        />
      </div>
    </div>
  );
}

// Category Section Component (for landing page)
interface CategorySectionProps {
  categoryKey: string;
  data: CategoryData;
}

function CategorySection({ categoryKey, data }: CategorySectionProps): ReactElement {
  const config = CATEGORY_CONFIG[categoryKey] || CATEGORY_CONFIG.overview;

  if (data.articles.length === 0) {
    return <></>;
  }

  return (
    <section className="mb-12">
      {/* Section Header */}
      <div className="flex items-center justify-between mb-5">
        <div className="flex items-center gap-3">
          <div className={`w-10 h-10 rounded-lg ${config.bgColor} ${config.color} flex items-center justify-center`}>
            {config.icon}
          </div>
          <div>
            <h2 className="text-xl font-semibold text-slate-800" style={{ fontFamily: 'Georgia, serif' }}>
              {data.name}
            </h2>
            <p className="text-sm text-slate-500 mt-0.5">{data.description}</p>
          </div>
          <span className="px-2.5 py-1 text-xs font-medium text-slate-500 bg-slate-100 rounded-full">
            {data.total_count} reports
          </span>
        </div>
        <Link
          to={`/reports/category/${categoryKey}`}
          className="flex items-center gap-1 text-sm font-medium text-slate-500 hover:text-slate-700 transition-colors"
        >
          View All
          <ChevronRight className="w-4 h-4" />
        </Link>
      </div>

      {/* Reports Grid */}
      <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-5">
        {data.articles.slice(0, 8).map((report) => (
          <ReportCard key={report.id} report={report} />
        ))}
      </div>
    </section>
  );
}

// Pagination Component
interface PaginationProps {
  page: number;
  totalPages: number;
  onPageChange: (page: number) => void;
  isLoading: boolean;
}

function Pagination({ page, totalPages, onPageChange, isLoading }: PaginationProps): ReactElement {
  if (totalPages <= 1) return <></>;

  const pages = [];
  const showEllipsisStart = page > 3;
  const showEllipsisEnd = page < totalPages - 2;

  // Always show first page
  pages.push(1);

  if (showEllipsisStart) {
    pages.push(-1); // ellipsis marker
  }

  // Show pages around current
  for (let i = Math.max(2, page - 1); i <= Math.min(totalPages - 1, page + 1); i++) {
    if (!pages.includes(i)) {
      pages.push(i);
    }
  }

  if (showEllipsisEnd) {
    pages.push(-2); // ellipsis marker
  }

  // Always show last page
  if (totalPages > 1 && !pages.includes(totalPages)) {
    pages.push(totalPages);
  }

  return (
    <div className="flex items-center justify-center gap-2 mt-8">
      <button
        onClick={() => onPageChange(page - 1)}
        disabled={page === 1 || isLoading}
        className="p-2 rounded-lg border border-slate-200 text-slate-600 hover:bg-slate-50 disabled:opacity-50 disabled:cursor-not-allowed"
      >
        <ChevronLeft className="w-5 h-5" />
      </button>

      {pages.map((p, idx) => (
        p < 0 ? (
          <span key={`ellipsis-${idx}`} className="px-2 text-slate-400">...</span>
        ) : (
          <button
            key={p}
            onClick={() => onPageChange(p)}
            disabled={isLoading}
            className={`px-4 py-2 rounded-lg text-sm font-medium transition-colors ${
              p === page
                ? 'bg-slate-800 text-white'
                : 'border border-slate-200 text-slate-600 hover:bg-slate-50'
            }`}
          >
            {p}
          </button>
        )
      ))}

      <button
        onClick={() => onPageChange(page + 1)}
        disabled={page === totalPages || isLoading}
        className="p-2 rounded-lg border border-slate-200 text-slate-600 hover:bg-slate-50 disabled:opacity-50 disabled:cursor-not-allowed"
      >
        <ChevronRight className="w-5 h-5" />
      </button>
    </div>
  );
}

// Main Portal Component
export default function ReportsPortal(): ReactElement {
  // View state: 'landing' or 'list'
  const [view, setView] = useState<'landing' | 'list'>('landing');

  // Landing page state
  const [portalData, setPortalData] = useState<PortalData | null>(null);

  // List view state
  const [listData, setListData] = useState<ListResponse | null>(null);
  const [selectedCategory, setSelectedCategory] = useState<string | null>(null);

  // Common state
  const [isLoading, setIsLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [searchQuery, setSearchQuery] = useState('');
  const [searchDebounce, setSearchDebounce] = useState('');
  const [tickerOnly, setTickerOnly] = useState(true);

  // Debounce search
  useEffect(() => {
    const timer = setTimeout(() => {
      setSearchDebounce(searchQuery);
    }, 300);
    return () => clearTimeout(timer);
  }, [searchQuery]);

  // Fetch portal data (landing page)
  const fetchPortalData = useCallback(async () => {
    setIsLoading(true);
    setError(null);
    try {
      const res = await fetch(`${API_BASE}/api/articles/portal?limit_per_category=8`);
      if (!res.ok) throw new Error('Failed to load portal data');
      const data = await res.json();
      setPortalData(data);
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Unknown error');
    } finally {
      setIsLoading(false);
    }
  }, []);

  // Fetch list data with filters
  const fetchListData = useCallback(async (page: number = 1) => {
    setIsLoading(true);
    setError(null);
    try {
      const params = new URLSearchParams();
      params.set('limit', '20');
      params.set('offset', String((page - 1) * 20));
      if (selectedCategory) params.set('category', selectedCategory);
      if (searchDebounce) {
        if (tickerOnly) {
          params.set('ticker', searchDebounce.toUpperCase());
        } else {
          params.set('search', searchDebounce);
        }
      }

      const res = await fetch(`${API_BASE}/api/articles/list?${params}`);
      if (!res.ok) throw new Error('Failed to load articles');
      const data = await res.json();
      setListData(data);
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Unknown error');
    } finally {
      setIsLoading(false);
    }
  }, [selectedCategory, searchDebounce, tickerOnly]);

  // Initial load
  useEffect(() => {
    if (view === 'landing') {
      fetchPortalData();
    }
  }, [view, fetchPortalData]);

  // Fetch list when filters change
  useEffect(() => {
    if (view === 'list') {
      fetchListData(1);
    }
  }, [view, selectedCategory, searchDebounce, fetchListData]);

  // Handle back to landing
  const handleBackToLanding = () => {
    setView('landing');
    setSelectedCategory(null);
    setSearchQuery('');
  };

  // Handle page change
  const handlePageChange = (page: number) => {
    fetchListData(page);
    window.scrollTo({ top: 0, behavior: 'smooth' });
  };

  // Clear filters
  const clearFilters = () => {
    setSelectedCategory(null);
    setSearchQuery('');
  };

  // Category display order
  const categoryOrder = ['market', 'institutional', 'valuation', 'macro', 'risk', 'overview', 'classification', 'historical', 'fundamentals', 'sentiment', 'context'];

  // Get total count
  const totalCount = view === 'landing'
    ? portalData?.total_articles || 0
    : listData?.total_count || 0;

  // Collect all visible report IDs so the batch endpoint checks them in one call.
  const batchItems = (() => {
    const acc: { itemType: 'report'; itemId: string }[] = [];
    if (view === 'landing' && portalData) {
      Object.values(portalData.categories ?? {}).forEach((cat: CategoryData) => {
        cat.articles.forEach((r) => {
          const id = reportAnalysisId(r.path || `/reports/report_${r.id}`);
          if (id) acc.push({ itemType: 'report', itemId: id });
        });
      });
    } else if (listData) {
      listData.articles.forEach((r) => {
        const id = reportAnalysisId(r.path || `/reports/report_${r.id}`);
        if (id) acc.push({ itemType: 'report', itemId: id });
      });
    }
    return acc;
  })();

  return (
    <BookmarkBatchProvider items={batchItems}>
    <div className="min-h-screen bg-slate-50">
      {/* Header */}
      <div className="bg-white border-b border-slate-200">
        <div className="max-w-7xl mx-auto px-6 py-8">
          <div className="flex items-start justify-between">
            <div>
              <div className="flex items-center gap-4 mb-2">
                {view === 'list' && (
                  <button
                    onClick={handleBackToLanding}
                    className="p-2 rounded-lg hover:bg-slate-100 text-slate-600 transition-colors"
                  >
                    <ChevronLeft className="w-5 h-5" />
                  </button>
                )}
                <div className="w-12 h-12 rounded-xl bg-gradient-to-br from-slate-700 to-slate-900 flex items-center justify-center shadow-sm">
                  <FileText className="w-6 h-6 text-white" />
                </div>
                <div>
                  <h1 className="text-3xl font-bold text-slate-800" style={{ fontFamily: 'Georgia, serif' }}>
                    {view === 'list' && selectedCategory
                      ? CATEGORY_CONFIG[selectedCategory]?.name || 'Reports'
                      : 'Financial Analysis Reports'
                    }
                  </h1>
                  {view === 'list' && selectedCategory && (
                    <p className="text-sm text-slate-500 mt-1">
                      {CATEGORY_CONFIG[selectedCategory]?.description}
                    </p>
                  )}
                </div>
              </div>
            </div>
            <div className="text-right">
              <span className="text-3xl font-bold text-slate-800">{totalCount}</span>
              <p className="text-sm text-slate-500">reports</p>
            </div>
          </div>

          {/* Search and Filters */}
          <div className="mt-6 flex items-center gap-4">
            {/* Search Bar */}
            <div className="flex-1 max-w-xl relative">
              <Search className="absolute left-4 top-1/2 -translate-y-1/2 w-5 h-5 text-slate-400" />
              <input
                type="text"
                placeholder={tickerOnly ? "Search by ticker (e.g. AAPL)..." : "Search by title or keyword..."}
                value={searchQuery}
                onChange={(e) => {
                  setSearchQuery(e.target.value);
                  if (view === 'landing' && e.target.value) {
                    setView('list');
                  }
                }}
                className="w-full pl-12 pr-4 py-3 bg-slate-50 border border-slate-200 rounded-lg text-slate-700 placeholder-slate-400 focus:outline-none focus:border-slate-400 focus:ring-2 focus:ring-slate-100 transition-all text-sm"
              />
            </div>

            {/* Ticker Only Toggle */}
            <label className="flex items-center gap-2 cursor-pointer select-none whitespace-nowrap">
              <input
                type="checkbox"
                checked={tickerOnly}
                onChange={(e) => setTickerOnly(e.target.checked)}
                className="w-4 h-4 rounded border-slate-300 text-slate-800 focus:ring-slate-200"
              />
              <span className="text-sm text-slate-600">Ticker only</span>
            </label>

            {/* Refresh Button */}
            <button
              onClick={() => view === 'landing' ? fetchPortalData() : fetchListData(listData?.page || 1)}
              disabled={isLoading}
              className="p-3 rounded-lg border border-slate-200 text-slate-600 hover:bg-slate-50 disabled:opacity-50 transition-colors"
            >
              <RefreshCw className={`w-5 h-5 ${isLoading ? 'animate-spin' : ''}`} />
            </button>
          </div>

          {/* Active Filters */}
          {view === 'list' && (selectedCategory || searchQuery) && (
            <div className="mt-4 flex items-center gap-2">
              <span className="text-sm text-slate-500">Filters:</span>
              {selectedCategory && (
                <span className="inline-flex items-center gap-1 px-3 py-1 bg-slate-100 text-slate-700 rounded-full text-sm">
                  {CATEGORY_CONFIG[selectedCategory]?.name}
                  <button onClick={() => setSelectedCategory(null)} className="hover:text-slate-900">
                    <X className="w-3 h-3" />
                  </button>
                </span>
              )}
              {searchQuery && (
                <span className="inline-flex items-center gap-1 px-3 py-1 bg-slate-100 text-slate-700 rounded-full text-sm">
                  "{searchQuery}"
                  <button onClick={() => setSearchQuery('')} className="hover:text-slate-900">
                    <X className="w-3 h-3" />
                  </button>
                </span>
              )}
              <button
                onClick={clearFilters}
                className="text-sm text-slate-500 hover:text-slate-700 underline"
              >
                Clear all
              </button>
            </div>
          )}
        </div>
      </div>

      {/* Content */}
      <div className="max-w-7xl mx-auto px-6 py-8">
        {/* Loading State */}
        {isLoading && !portalData && !listData && (
          <div className="flex flex-col items-center justify-center py-20">
            <Loader2 className="w-10 h-10 animate-spin text-slate-400 mb-4" />
            <p className="text-slate-500">Loading reports...</p>
          </div>
        )}

        {/* Error State */}
        {error && (
          <div className="text-center py-20">
            <div className="w-16 h-16 mx-auto mb-4 rounded-full bg-red-50 flex items-center justify-center">
              <FileText className="w-8 h-8 text-red-400" />
            </div>
            <h3 className="text-lg font-medium text-slate-700 mb-2">Failed to load reports</h3>
            <p className="text-sm text-slate-500 mb-4">{error}</p>
            <button
              onClick={() => view === 'landing' ? fetchPortalData() : fetchListData()}
              className="px-4 py-2 bg-slate-800 text-white rounded-lg hover:bg-slate-700 transition-colors"
            >
              Try Again
            </button>
          </div>
        )}

        {/* Landing View - Categories */}
        {view === 'landing' && portalData && !isLoading && (
          <>
            {categoryOrder
              .filter((cat) => portalData.categories[cat]?.articles.length > 0)
              .map((categoryKey) => (
                <CategorySection
                  key={categoryKey}
                  categoryKey={categoryKey}
                  data={portalData.categories[categoryKey]}
                />
              ))}

            {Object.values(portalData.categories).every(c => c.articles.length === 0) && (
              <div className="text-center py-20">
                <div className="w-16 h-16 mx-auto mb-4 rounded-full bg-slate-100 flex items-center justify-center">
                  <FileText className="w-8 h-8 text-slate-400" />
                </div>
                <h3 className="text-lg font-medium text-slate-700 mb-2">No reports yet</h3>
                <p className="text-sm text-slate-500">Check back later for new reports</p>
              </div>
            )}
          </>
        )}

        {/* List View - Paginated Grid */}
        {view === 'list' && listData && (
          <>
            {/* Results summary */}
            <div className="mb-6 text-sm text-slate-500">
              Showing {listData.offset + 1}-{Math.min(listData.offset + listData.articles.length, listData.total_count)} of {listData.total_count} reports
            </div>

            {/* Grid */}
            {listData.articles.length > 0 ? (
              <>
                <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-5">
                  {listData.articles.map((report) => (
                    <ReportCard key={report.id} report={report} />
                  ))}
                </div>

                {/* Pagination */}
                <Pagination
                  page={listData.page}
                  totalPages={listData.total_pages}
                  onPageChange={handlePageChange}
                  isLoading={isLoading}
                />
              </>
            ) : (
              <div className="text-center py-20">
                <div className="w-16 h-16 mx-auto mb-4 rounded-full bg-slate-100 flex items-center justify-center">
                  <FileText className="w-8 h-8 text-slate-400" />
                </div>
                <h3 className="text-lg font-medium text-slate-700 mb-2">No matching reports</h3>
                <p className="text-sm text-slate-500 mb-4">Try adjusting your filters or search terms</p>
                <button
                  onClick={clearFilters}
                  className="px-4 py-2 bg-slate-800 text-white rounded-lg hover:bg-slate-700 transition-colors"
                >
                  Clear Filters
                </button>
              </div>
            )}
          </>
        )}

        {/* Loading overlay for subsequent loads */}
        {isLoading && (portalData || listData) && (
          <div className="fixed inset-0 bg-white/50 flex items-center justify-center z-50">
            <Loader2 className="w-10 h-10 animate-spin text-slate-600" />
          </div>
        )}
      </div>
    </div>
    </BookmarkBatchProvider>
  );
}
